/**
 * `/catalog` — каталог лендинга (№431, спека 2026-10-28-catalog-autopairs-design.md): автоподбор пары
 * «платье — референс» (+ лицо) для каждого платья, проверка человеком, пакетная генерация одобренных.
 * Пары хранит сервер (`catalog_pair`); запуск — прежний `POST /api/fittings` по одной паре из очереди на
 * фронте («Остановить» — после текущей пары, как в «Импорт топ»). Ключ идемпотентности пары — UUIDv5
 * с прежней генерацией строки: двойной клик не платит дважды, «Повторить» после упавшей — новый запуск.
 * Пока идёт подбор, строки и запуск заперты: подбор пишет пары фоном (сервер чужую правку не затрёт,
 * но сотрудник не должен одобрять то, что через секунду сменится).
 * Вкладки (№441, №447): «На проверку» — пары до запуска и полоса запуска, «В работе» — запущенные пары тремя
 * картинками «исходник — референс — генерация»; запуск переносит пару на вторую вкладку сам (`launched`).
 * На «В работе» у генерации «Брак» и «Удачная» (окно кадра 3:4 с приближением → метка и улучшение по рамке);
 * брак и упавшую переделывают на месте — другой референс и новый запуск; утверждённые — на «Утверждены»
 * с улучшенным файлом.
 * Клик по любой картинке пары на «В работе» открывает крупное окно сравнения (№459); «Удачная» из него — не метка
 * кадра целиком, а та же рамка 3:4, что у кнопки под генерацией.
 */
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Ban, Download, HeartOff, Play, RefreshCw, Sparkles, ThumbsUp, Undo2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { CompareTriptych, type CompareHost } from "@/components/results/CompareTriptych";
import { RefPickerDialog } from "@/components/fitting/RefPickerDialog";
import { CropFrame } from "@/components/posts/CropFrame";
import { RetryImg } from "@/components/trends/PinGrid";
import { btn, btnGhost, btnPrimary, Empty, ErrorLine, Loading, Panel, Segmented } from "@/components/trends/ui";
import {
  approvedRows,
  canRedo,
  catalogTab,
  counters,
  isPending,
  launchBody,
  launchedLabel,
  needsPolling,
  pairKey,
  pickedVariant,
  runPairs,
  splitTabs,
  summarizePairs,
  type CatalogData,
  type CatalogRow,
  type CatalogTab,
  type CatalogVariant,
  type PairStep,
} from "@/lib/catalog";
import { loadCatalog, markLaunched, nextRef, patchPair, pickVariant, startPick } from "@/lib/catalogApi";
import { ApiFailure } from "@/lib/api";
import { estimateFittings, launchFittings } from "@/lib/fitting";
import { mediaUrl, thumbUrl } from "@/lib/media";
import { loadResultCard, resultKeys, resultPath, RESULTS_PREFIX } from "@/lib/results";
import { retryQuery } from "@/lib/trends";
import type { Crop } from "@/lib/postPlan";
import { enhanceNote, markLabel } from "@/lib/verdict";
import { Dialog } from "@/shared/ui/Dialog";

const KEY = ["catalog"] as const;
const NO_PINS: readonly number[] = [];

const money = (n: number) => `$${n.toFixed(2)}`;
const pct = (s: number | null) => (s === null ? "" : `${Math.round(s * 100)}%`);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function CatalogPage() {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: KEY,
    queryFn: loadCatalog,
    retry: retryQuery,
    refetchInterval: (query) => (query.state.data && needsPolling(query.state.data) ? 3000 : false),
  });
  const data = q.data;
  const [search, setSearch] = useSearchParams();
  const tab = catalogTab(search);
  const [running, setRunning] = useState(false);
  const [view, setView] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const replaceRow = useCallback(
    (row: CatalogRow) =>
      client.setQueryData<CatalogData>(KEY, (d) => {
        if (!d) return d;
        const rows = d.rows.map((r) => (r.dress_id === row.dress_id ? row : r));
        return { ...d, rows, counts: counters(rows) };
      }),
    [client],
  );

  const act = useCallback(
    async (fn: () => Promise<CatalogRow | void>) => {
      setActionError(null);
      try {
        const row = await fn();
        if (row) replaceRow(row);
      } catch (e) {
        setActionError(errMsg(e));
        void client.invalidateQueries({ queryKey: KEY });
      }
    },
    [client, replaceRow],
  );

  const pick = async (redo: boolean) => {
    setActionError(null);
    try {
      await startPick({ redo });
    } catch (e) {
      setActionError(errMsg(e));
    }
    void client.invalidateQueries({ queryKey: KEY });
  };

  if (q.isPending) return <Loading />;
  if (!data) return <ErrorLine error={q.error} />;

  const { review, gens, approved, skipped } = splitTabs(data.rows);
  const c = data.counts;
  const picking = data.picking;
  const busy = running || picking !== null;

  return (
    <div className="mx-auto grid max-w-[1000px] gap-4 p-5 pb-24" data-annot="каталог">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented<CatalogTab>
          label=""
          annot="вкладки каталога"
          value={tab}
          onPick={(t) => setSearch(t === "review" ? {} : { tab: t }, { replace: true })}
          options={[
            { value: "review", label: `На проверку ${review.length}` },
            { value: "gens", label: `В работе ${gens.length}` },
            { value: "approved", label: `Утверждены ${approved.length}` },
          ]}
        />
        <span className="text-[12.5px] text-fg-muted tnum">
          кандидатов {c.candidates} · с парой {c.paired} · одобрено {c.approved}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button type="button" className={btn} disabled={picking !== null || running} onClick={() => void pick(false)} title="Подобрать пары платьям без пары">
            <RefreshCw size={13} />
            Подобрать
          </button>
          <button
            type="button"
            className={btn}
            disabled={picking !== null || running}
            title="Подобрать заново для всех, кроме одобренных и запущенных"
            onClick={() => {
              if (window.confirm("Подобрать заново для всех платьев, кроме одобренных и запущенных?")) void pick(true);
            }}
          >
            Заново для всех
          </button>
        </div>
      </div>
      {picking ? (
        <div className="text-[12.5px] text-fg-muted tnum">
          Подбор идёт: {picking.done} из {picking.total}…
        </div>
      ) : null}
      {data.faces.length === 0 ? (
        <div className="text-[12.5px] text-warn">В библиотеке «Внешность модели» нет лиц — пары пойдут без лица.</div>
      ) : null}
      <ErrorLine error={q.isError ? q.error : actionError} />

      {tab === "gens" ? (
        <>
          {gens.length === 0 ? <Empty>Запущенных пар пока нет — одобрите пары на вкладке «На проверку» и запустите.</Empty> : null}
          <div className="grid gap-3">
            {gens.map((r) => (
              <GenCard
                key={r.dress_id}
                row={r}
                data={data}
                busy={busy}
                onView={setView}
                act={act}
                replaceRow={replaceRow}
                onNote={setActionError}
              />
            ))}
          </div>
        </>
      ) : null}

      {tab === "approved" ? (
        <>
          {approved.length === 0 ? <Empty>Утверждённых пока нет — отметьте удачную генерацию на вкладке «В работе».</Empty> : null}
          <div className="grid gap-3">
            {approved.map((r) => (
              <ApprovedCard key={r.dress_id} row={r} busy={busy} onView={setView} act={act} />
            ))}
          </div>
        </>
      ) : null}

      {tab === "review" && review.length === 0 && !picking ? (
        <Empty>Платьев на проверку нет, либо подбор ещё не запускали — нажмите «Подобрать».</Empty>
      ) : null}
      {tab === "review" ? (
        <div className="grid gap-3">
          {review.map((r) => (
            <PairRow key={r.dress_id} row={r} data={data} busy={busy} onView={setView} act={act} />
          ))}
        </div>
      ) : null}

      {tab === "review" && skipped.length ? (
        <Panel title={`Не берём (${skipped.length})`}>
          <div className="grid gap-1.5">
            {skipped.map((r) => (
              <div key={r.dress_id} className="flex items-center gap-2 text-[12.5px] text-fg-muted">
                <span className="truncate">{r.dress_name ?? `Платье #${r.dress_id}`}</span>
                <button
                  type="button"
                  className={clsx(btnGhost, "ml-auto")}
                  disabled={busy}
                  onClick={() => void act(() => patchPair(r.dress_id, { state: "proposed" }))}
                >
                  <Undo2 size={13} />
                  вернуть
                </button>
              </div>
            ))}
          </div>
        </Panel>
      ) : null}

      {/* полоса живёт и на других вкладках (скрыта): размонтирование остановило бы идущий запуск */}
      <LaunchBar
        hidden={tab !== "review" && !running}
        rows={data.rows}
        data={data}
        running={running}
        picking={picking !== null}
        setRunning={setRunning}
        replaceRow={replaceRow}
      />

      <Dialog open={view !== null} title="Просмотр" onClose={() => setView(null)} wide>
        {view ? <img src={mediaUrl(view)} alt="" className="mx-auto max-h-[80vh] object-contain" /> : null}
      </Dialog>
    </div>
  );
}

function PairRow({
  row,
  data,
  busy,
  onView,
  act,
}: {
  row: CatalogRow;
  data: CatalogData;
  busy: boolean;
  onView: (path: string) => void;
  act: (fn: () => Promise<CatalogRow | void>) => Promise<void>;
}) {
  const [picker, setPicker] = useState(false);
  const [noMore, setNoMore] = useState(false);
  // «кандидаты закончились» относится к текущему референсу: сменили его (в т.ч. «Выбрать…») — сброс
  useEffect(() => setNoMore(false), [row.ref?.id]);
  const launched = row.state === "launched";
  const locked = busy || launched || isPending(row);
  const approved = row.state === "approved";
  const takenRefs = useMemo(() => (row.ref ? [row.ref.id] : []), [row.ref]);

  const other = () =>
    void act(async () => {
      try {
        return await nextRef(row.dress_id);
      } catch (e) {
        if (e instanceof ApiFailure && e.items[0]?.code === "NO_MORE") {
          setNoMore(true);
          return;
        }
        throw e;
      }
    });

  return (
    <div className={clsx("grid gap-2 rounded-[var(--radius-card)] border bg-bg-subtle p-3", approved ? "border-accent" : "border-border")}>
      <div className="flex flex-wrap items-start gap-3">
        <Pic path={row.dress_thumb} label="платье" onView={onView} />
        {row.ref ? (
          <Pic path={row.ref.asset_path} label={row.ref.library_name ?? "референс"} badge={pct(row.ref.score)} onView={onView} />
        ) : (
          <div className="grid h-[150px] w-[100px] place-items-center rounded-[4px] border border-dashed border-border text-center text-[11.5px] text-fg-faint">
            пары нет
          </div>
        )}
        <div className="grid min-w-[220px] flex-1 content-start gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[13.5px] font-medium text-fg">{row.dress_name ?? `Платье #${row.dress_id}`}</span>
            {launched ? <span className="text-[12px] text-accent-text">запущено</span> : null}
          </div>
          {row.error ? <div className="text-[12px] text-warn">{row.error}</div> : null}
          {row.generation ? (
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-fg-muted tnum">
              <span>{launchedLabel(row.generation)}</span>
              <Link to="/results" target="_blank" className="text-accent-text hover:underline">
                Результаты ↗
              </Link>
            </div>
          ) : null}
          <Faces row={row} data={data} disabled={locked} act={act} onView={onView} />
          <div className="flex flex-wrap items-center gap-2">
            <label className={clsx("flex items-center gap-1.5 text-[12.5px]", locked || !row.ref ? "text-fg-faint" : "text-fg")}>
              <input
                type="checkbox"
                checked={approved || launched}
                disabled={locked || !row.ref}
                onChange={(e) => void act(() => patchPair(row.dress_id, { state: e.target.checked ? "approved" : "proposed" }))}
              />
              годится
            </label>
            <button type="button" className={btn} disabled={locked || row.candidates_left === 0 || noMore} onClick={other}>
              Другой референс
            </button>
            <button type="button" className={btn} disabled={locked} onClick={() => setPicker(true)}>
              Выбрать…
            </button>
            <button
              type="button"
              className={btnGhost}
              disabled={locked}
              onClick={() => void act(() => patchPair(row.dress_id, { state: "skipped" }))}
            >
              <Ban size={13} />
              Не брать в каталог
            </button>
            {noMore || (row.ref && row.candidates_left === 0) ? (
              <span className="text-[11.5px] text-fg-faint">кандидаты закончились</span>
            ) : null}
          </div>
        </div>
      </div>
      <RefPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        refsOnly
        single
        takenPins={NO_PINS}
        takenRefs={takenRefs}
        title="Выбрать референс"
        dresses={[{ id: row.dress_id, title: row.dress_name ?? `Платье #${row.dress_id}`, photoIds: row.photo_ids, thumbPath: row.dress_thumb }]}
        onAdd={(_pins, refs) => {
          const id = refs[0];
          if (id !== undefined) void act(() => patchPair(row.dress_id, { reference_item_id: id }));
        }}
      />
    </div>
  );
}

function Pic({ path, label, badge, onView }: { path: string | null; label: string; badge?: string; onView: (p: string) => void }) {
  return (
    <div className="grid w-[100px] gap-1">
      <button
        type="button"
        disabled={!path}
        onClick={() => path && onView(path)}
        className="relative block aspect-[2/3] overflow-hidden rounded-[4px] border border-border bg-bg-elevated"
      >
        {path ? <RetryImg src={thumbUrl(path)} alt={label} className="absolute inset-0 h-full w-full object-cover" /> : null}
        {badge ? <span className="absolute bottom-1 right-1 rounded bg-bg/85 px-1 text-[11px] text-fg tnum">{badge}</span> : null}
      </button>
      <span className="truncate text-[11px] text-fg-faint" title={label}>
        {label}
      </span>
    </div>
  );
}

/** Запущенная пара (№441): исходник — референс — генерация в ряд одной высоты; клик по любой — окно сравнения (№459).
 * №447: под генерацией «Брак» / «Удачная», брак приглушён и подписан (№450, как плитка «Результатов»);
 * брак, упавшую и пустую переделывают тут же — блок «Переделать». */
function GenCard({
  row,
  data,
  busy,
  onView,
  act,
  replaceRow,
  onNote,
}: {
  row: CatalogRow;
  data: CatalogData;
  busy: boolean;
  onView: (path: string) => void;
  act: (fn: () => Promise<CatalogRow | void>) => Promise<void>;
  replaceRow: (r: CatalogRow) => void;
  onNote: (text: string) => void;
}) {
  const client = useQueryClient();
  const [approve, setApprove] = useState<CatalogVariant | null>(null);
  // окно держит id, а не вариант: исчез из строки после обновления — окна нет (№459)
  const [compareId, setCompareId] = useState<number | null>(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  // запуск «Переделать» идёт — «Удачная» сделала бы платье не кандидатом, и оплаченная генерация осталась бы без пары
  const [launching, setLaunching] = useState(false);
  const g = row.generation;
  const variants = g?.variants ?? [];
  const genRef = g?.ref ?? row.ref;
  const pending = isPending(row);
  const stub = !g ? "генерации нет" : pending ? "в работе…" : g.status === "done" ? "без результата" : "ошибка";
  const first = variants[0];
  const compare = variants.find((v) => v.id === compareId) ?? null;
  // «Брак» (в т.ч. из окна) заперт на время запроса, ошибка уходит и в окно (№459)
  const locked = busy || pending || launching || marking;
  const mark = (v: CatalogVariant) =>
    void act(async () => {
      setMarking(true);
      setMarkError(null);
      try {
        await pickVariant(v.id, "rejected");
        await client.invalidateQueries({ queryKey: KEY });
        // окно сравнения и карточка «Результатов» держат отметку в своём кэше (№459)
        await client.invalidateQueries({ queryKey: RESULTS_PREFIX });
      } catch (e) {
        setMarkError(errMsg(e));
        throw e;
      } finally {
        setMarking(false);
      }
    });
  return (
    <div className="grid gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot="генерация каталога">
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-fg-muted tnum">
        <span className="text-[13.5px] font-medium text-fg">{row.dress_name ?? `Платье #${row.dress_id}`}</span>
        {g ? <span>{launchedLabel(g)}</span> : null}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Frame
          title="Исходник"
          path={row.dress_thumb}
          hint={first ? "Сравнить крупно" : undefined}
          onClick={() => (first ? setCompareId(first.id) : row.dress_thumb && onView(row.dress_thumb))}
        />
        <Frame
          title={genRef?.library_name ? `Референс · ${genRef.library_name}` : "Референс"}
          path={genRef?.asset_path ?? null}
          hint={first ? "Сравнить крупно" : undefined}
          onClick={() => (first ? setCompareId(first.id) : genRef && onView(genRef.asset_path))}
        />
        {variants.length ? (
          variants.map((v) => {
            const rejected = v.pick === "rejected";
            return (
              <div key={v.id} className="grid min-w-0 content-start gap-1.5">
                <Frame
                  title={["Генерация", markLabel("reference", v.pick)].filter(Boolean).join(" · ")}
                  path={v.asset_path}
                  hint="Сравнить крупно"
                  onClick={() => setCompareId(v.id)}
                  rejected={rejected}
                />
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    className={clsx(btn, rejected && "border-err text-err")}
                    disabled={locked}
                    aria-pressed={rejected}
                    title={rejected ? "снять «Брак»" : "Брак: генерация не годится — её можно переделать"}
                    onClick={() => mark(v)}
                  >
                    <HeartOff size={13} className={rejected ? "fill-current" : undefined} />
                    Брак
                  </button>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={locked || !row.in_catalog}
                    title="Удачная: кадрировать 3:4, утвердить и отправить на улучшение"
                    onClick={() => setApprove(v)}
                  >
                    <ThumbsUp size={13} />
                    Удачная
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <Frame title="Генерация" path={null} stub={stub} />
        )}
      </div>
      {canRedo(row) ? (
        <RedoBar
          row={row}
          data={data}
          busy={busy}
          launching={launching}
          setLaunching={setLaunching}
          onView={onView}
          act={act}
          replaceRow={replaceRow}
        />
      ) : null}
      <ApproveDialog variant={approve} onClose={() => setApprove(null)} onNote={onNote} />
      {compare ? (
        <GenCompare
          variantId={compare.id}
          host={{
            // лайк окна НЕ шлёт pick: тот сразу платит за улучшение всего кадра, а тут нужна рамка 3:4 (№459)
            onPick: (status) => {
              if (status === "picked") {
                setCompareId(null);
                setMarkError(null);
                setApprove(compare);
              } else mark(compare);
            },
            busy: locked,
            error: markError,
            canLike: row.in_catalog,
            likeHint: "Удачная: кадрировать 3:4, утвердить и отправить на улучшение",
          }}
          onClose={() => {
            setCompareId(null);
            setMarkError(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** Окно сравнения (№459): карточка результата грузится по варианту, дальше — общий `CompareTriptych` с `host`. */
function GenCompare({ variantId, host, onClose }: { variantId: number; host: CompareHost; onClose: () => void }) {
  const q = useQuery({ queryKey: resultKeys.card(variantId), queryFn: () => loadResultCard(variantId), retry: retryQuery });
  if (q.data) return <CompareTriptych card={q.data} host={host} onClose={onClose} />;
  return (
    <Dialog open title="Сравнение" onClose={onClose}>
      {q.isError ? <ErrorLine error={q.error} /> : <Loading />}
    </Dialog>
  );
}

/** «Удачная» (№447): рамка 3:4 с приближением → метка «В улучшение» и улучшение Krea по рамке.
 * Метка переносит пару на «Утверждены» и размонтирует карточку с окном — что вышло с улучшением, показывает
 * страница (`onNote`); ошибка самой метки (плохая рамка, сеть) остаётся в окне. */
function ApproveDialog({
  variant,
  onClose,
  onNote,
}: {
  variant: CatalogVariant | null;
  onClose: () => void;
  onNote: (text: string) => void;
}) {
  const client = useQueryClient();
  const [crop, setCrop] = useState<Crop | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setCrop(null);
    setError(null);
  }, [variant?.id]);
  const confirm = async () => {
    if (!variant) return;
    setBusy(true);
    setError(null);
    try {
      const out = await pickVariant(variant.id, "picked", crop);
      const failed = out.enhance && "error" in out.enhance ? enhanceNote(out.enhance) : null;
      const stale = crop && out.enhance && "started" in out.enhance && !out.enhance.started;
      onClose();
      if (failed) onNote(failed);
      else if (stale) onNote("Фото уже улучшали: новая рамка не применена, второй оплаты нет.");
      await client.invalidateQueries({ queryKey: KEY });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={variant !== null} title="Удачная — кадр перед улучшением" onClose={onClose}>
      {variant ? (
        <div className="grid gap-3 p-3" data-annot="кадр перед улучшением">
          <CropFrame src={mediaUrl(variant.asset_path)} width={null} height={null} aspect="3:4" crop={crop} zoomable onCommit={setCrop} />
          <div className="text-[12px] text-fg-muted">
            Рамка 3:4: перетащите её, ползунком приблизьте кадр, если вокруг модели много лишнего фона. Улучшение пойдёт
            по рамке; оригинал остаётся в «Результатах». Фото, которое уже улучшали, второй раз не улучшается.
          </div>
          <ErrorLine error={error} />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btn} onClick={onClose}>
              Отмена
            </button>
            <button type="button" className={clsx(btnPrimary, "ml-auto")} disabled={busy} onClick={() => void confirm()}>
              <Sparkles size={14} />
              {busy ? "отправляю…" : "Утвердить и улучшить ≈ $0.24"}
            </button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

/** «Переделать» (№447): другой референс (или тот же) и новая генерация прямо на «В работе». Референс пары меняется
 * сразу (сервер держит пару `launched`), ключ запуска — от прежней генерации: двойной клик не платит дважды. */
function RedoBar({
  row,
  data,
  busy,
  launching,
  setLaunching,
  onView,
  act,
  replaceRow,
}: {
  row: CatalogRow;
  data: CatalogData;
  busy: boolean;
  launching: boolean;
  setLaunching: (on: boolean) => void;
  onView: (path: string) => void;
  act: (fn: () => Promise<CatalogRow | void>) => Promise<void>;
  replaceRow: (r: CatalogRow) => void;
}) {
  const client = useQueryClient();
  const [picker, setPicker] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setNoMore(false), [row.ref?.id]);
  const face = row.face ? (data.faces.find((f) => f.id === row.face!.id) ?? null) : null;
  const changed = row.ref !== null && row.ref.id !== row.generation?.ref?.id;
  const takenRefs = useMemo(() => (row.ref ? [row.ref.id] : []), [row.ref]);
  const body = row.ref ? (({ key: _k, ...b }) => b)(launchBody(row, face, "")) : null;
  const est = useQuery({
    queryKey: ["catalog-estimate", body] as const,
    queryFn: async () => (await estimateFittings(body!)).total,
    enabled: body !== null,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const cost = est.data !== undefined ? ` ≈ ${money(est.data)}` : "";

  const other = () =>
    void act(async () => {
      try {
        return await nextRef(row.dress_id);
      } catch (e) {
        if (e instanceof ApiFailure && e.items[0]?.code === "NO_MORE") {
          setNoMore(true);
          return;
        }
        throw e;
      }
    });

  const launch = async () => {
    if (!row.ref) return;
    if (!window.confirm(`Сгенерировать заново${cost}? Это платно.`)) return;
    setLaunching(true);
    setError(null);
    try {
      if (row.face && !face) throw new Error("лицо пары не найдено в библиотеке");
      const key = await pairKey(row.dress_id, row.ref.id, row.face?.id ?? null, row.generation?.id ?? null);
      const res = await launchFittings(launchBody(row, face, key));
      const gen = res.generations[0];
      if (!gen) throw new Error(res.errors[0] ?? "сервер не создал генерацию");
      replaceRow(await markLaunched(row.dress_id, gen.generation_id));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLaunching(false);
      void client.invalidateQueries({ queryKey: KEY });
    }
  };

  const locked = busy || launching;
  return (
    <div className="grid gap-2 border-t border-border pt-2" data-annot="переделать генерацию">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] font-medium text-fg">Переделать</span>
        {changed && row.ref ? (
          <button
            type="button"
            onClick={() => onView(row.ref!.asset_path)}
            className="h-[60px] w-[40px] overflow-hidden rounded-[4px] border border-accent"
            title="новый референс — крупно"
          >
            <RetryImg src={thumbUrl(row.ref.asset_path)} alt="новый референс" className="h-full w-full object-cover" />
          </button>
        ) : null}
        <span className="text-[12px] text-fg-muted">
          {changed ? `новый референс${row.ref?.library_name ? ` · ${row.ref.library_name}` : ""}` : "референс прежний — смените или запустите ещё раз"}
        </span>
        <button type="button" className={btn} disabled={locked || row.candidates_left === 0 || noMore} onClick={other}>
          Другой референс
        </button>
        <button type="button" className={btn} disabled={locked} onClick={() => setPicker(true)}>
          Выбрать…
        </button>
        <button type="button" className={clsx(btnPrimary, "ml-auto")} disabled={locked || !row.ref} onClick={() => void launch()}>
          <Play size={14} />
          {launching ? "запускаю…" : `Сгенерировать заново${cost}`}
        </button>
      </div>
      {noMore || (row.ref && row.candidates_left === 0) ? <span className="text-[11.5px] text-fg-faint">кандидаты закончились — «Выбрать…»</span> : null}
      <ErrorLine error={error} />
      <RefPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        refsOnly
        single
        takenPins={NO_PINS}
        takenRefs={takenRefs}
        title="Выбрать референс"
        dresses={[{ id: row.dress_id, title: row.dress_name ?? `Платье #${row.dress_id}`, photoIds: row.photo_ids, thumbPath: row.dress_thumb }]}
        onAdd={(_pins, refs) => {
          const id = refs[0];
          if (id !== undefined) void act(() => patchPair(row.dress_id, { reference_item_id: id }));
        }}
      />
    </div>
  );
}

const ENHANCE_STUB: Record<string, string> = { queued: "улучшение в очереди…", running: "улучшается…", cancelled: "улучшение отменено" };

/** «Утверждены» (№447): исходник — утверждённая генерация — улучшенная (её файл и скачивают для каталога). */
function ApprovedCard({
  row,
  busy,
  onView,
  act,
}: {
  row: CatalogRow;
  busy: boolean;
  onView: (path: string) => void;
  act: (fn: () => Promise<CatalogRow | void>) => Promise<void>;
}) {
  const client = useQueryClient();
  const v = pickedVariant(row);
  if (!v) return null;
  const e = v.enhance;
  const ready = e?.status === "done" && e.asset_path ? e.asset_path : null;
  const stub = !e
    ? "улучшение не запускалось — откройте карточку"
    : e.status === "failed"
      ? `ошибка улучшения${e.error ? `: ${e.error}` : ""} — повторить в карточке`
      : (ENHANCE_STUB[e.status] ?? e.status);
  return (
    <div className="grid gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot="утверждённая генерация">
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-fg-muted">
        <span className="text-[13.5px] font-medium text-fg">{row.dress_name ?? `Платье #${row.dress_id}`}</span>
        <span className="text-accent-text">Утверждено</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {ready ? (
            <a className={btnPrimary} href={mediaUrl(ready)} download>
              <Download size={14} />
              Скачать
            </a>
          ) : null}
          <button
            type="button"
            className={btnGhost}
            disabled={busy}
            title="Вернуть генерацию на «В работе» (улучшение остаётся)"
            onClick={() =>
              void act(async () => {
                await pickVariant(v.id, "none");
                await client.invalidateQueries({ queryKey: KEY });
              })
            }
          >
            <Undo2 size={13} />
            Снять утверждение
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Frame title="Исходник" path={row.dress_thumb} onClick={() => row.dress_thumb && onView(row.dress_thumb)} />
        <Frame title="Генерация" path={v.asset_path} href={resultPath(v.id)} />
        <Frame
          title="Улучшено"
          path={ready}
          href={e?.variant_id ? resultPath(e.variant_id) : resultPath(v.id)}
          stub={stub}
        />
      </div>
    </div>
  );
}

function Frame({
  title,
  path,
  onClick,
  href,
  stub,
  rejected,
  hint,
}: {
  title: string;
  path: string | null;
  onClick?: () => void;
  href?: string;
  stub?: string;
  /** №450: брак приглушён и подписан, как плитка «Результатов». */
  rejected?: boolean;
  /** `title` кнопки (№459); у ссылки остаётся прежняя подсказка. */
  hint?: string | undefined;
}) {
  const box = "relative block h-[320px] w-full overflow-hidden rounded-[4px] border border-border bg-bg-elevated";
  const img = path ? (
    <>
      <RetryImg src={thumbUrl(path)} alt={title} className={clsx("absolute inset-0 h-full w-full object-contain", rejected && "opacity-50")} />
      {rejected ? (
        <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded bg-bg/90 px-1.5 py-0.5 text-[11.5px] font-medium text-err">
          <HeartOff size={12} className="fill-current" />
          брак
        </span>
      ) : null}
    </>
  ) : null;
  return (
    <figure className="m-0 grid min-w-0 content-start gap-1">
      {href && path ? (
        <Link to={href} target="_blank" className={box} title="Открыть карточку изображения (там «Сравнить»)">
          {img}
        </Link>
      ) : path ? (
        <button type="button" className={box} title={hint} onClick={onClick}>
          {img}
        </button>
      ) : (
        <div className={clsx(box, "grid place-items-center text-[12px] text-fg-faint")}>{stub ?? "нет картинки"}</div>
      )}
      <figcaption className="truncate text-[11.5px] text-fg-muted" title={title}>
        {title}
      </figcaption>
    </figure>
  );
}

function Faces({
  row,
  data,
  disabled,
  act,
  onView,
}: {
  row: CatalogRow;
  data: CatalogData;
  disabled: boolean;
  act: (fn: () => Promise<CatalogRow | void>) => Promise<void>;
  onView: (p: string) => void;
}) {
  if (data.faces.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="лицо">
      <span className="text-[11.5px] text-fg-faint">лицо:</span>
      {data.faces.map((f) => {
        const on = row.face?.id === f.id;
        return (
          <button
            key={f.id}
            type="button"
            disabled={disabled}
            aria-pressed={on}
            title="выбрать лицо"
            onClick={() => void act(() => patchPair(row.dress_id, { face_id: f.id }))}
            onDoubleClick={() => onView(f.asset_path)}
            className={clsx("h-[44px] w-[32px] overflow-hidden rounded-[4px] border", on ? "border-accent ring-1 ring-accent" : "border-border")}
          >
            <RetryImg src={thumbUrl(f.asset_path)} alt="" className="h-full w-full object-cover" />
          </button>
        );
      })}
    </div>
  );
}

type Launch = { dress_id: number; row: CatalogRow };

function LaunchBar({
  hidden,
  rows,
  data,
  running,
  picking,
  setRunning,
  replaceRow,
}: {
  hidden: boolean;
  rows: CatalogRow[];
  data: CatalogData;
  running: boolean;
  picking: boolean;
  setRunning: (b: boolean) => void;
  replaceRow: (r: CatalogRow) => void;
}) {
  const client = useQueryClient();
  const approved = useMemo(() => approvedRows(rows), [rows]);
  const faceOf = useCallback(
    (r: CatalogRow) => (r.face ? (data.faces.find((f) => f.id === r.face!.id) ?? null) : null),
    [data.faces],
  );
  // Оценка — запросом на пару, ключ — тело запуска: переключили одну пару — пересчитывается только она
  const est = useQueries({
    queries: approved.map((r) => {
      const { key: _k, ...body } = launchBody(r, faceOf(r), "");
      return {
        queryKey: ["catalog-estimate", body] as const,
        queryFn: async () => (await estimateFittings(body)).total,
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        placeholderData: undefined,
      };
    }),
    combine: (rs) => ({
      data: rs.every((r) => r.data !== undefined && !r.isPlaceholderData)
        ? rs.reduce((a, r) => a + (r.data ?? 0), 0)
        : undefined,
      error: rs.find((r) => r.isError)?.error ?? null,
    }),
  });

  const stop = useRef(false);
  useEffect(
    () => () => {
      stop.current = true;
    },
    [],
  );
  const [progress, setProgress] = useState<{ i: number; of: number } | null>(null);
  const [steps, setSteps] = useState<PairStep[] | null>(null);
  const [stopped, setStopped] = useState(false);
  const names = useMemo(() => new Map(rows.map((r) => [r.dress_id, r.dress_name ?? `Платье #${r.dress_id}`])), [rows]);

  const start = async () => {
    const items: Launch[] = approved.map((row) => ({ dress_id: row.dress_id, row }));
    if (!items.length) return;
    const cost = est.data !== undefined ? ` ≈ ${money(est.data)}` : "";
    if (!window.confirm(`Запустить ${items.length} генераций${cost}? Каждая стоит денег.`)) return;
    stop.current = false;
    setSteps(null);
    setStopped(false);
    setRunning(true);
    setProgress({ i: 0, of: items.length });
    const all: PairStep[] = [];
    const out = await runPairs(
      items,
      async ({ row }) => {
        const face = faceOf(row);
        if (row.face && !face) throw new Error("лицо пары не найдено в библиотеке");
        const key = await pairKey(row.dress_id, row.ref!.id, row.face?.id ?? null, row.generation?.id ?? null);
        const res = await launchFittings(launchBody(row, face, key));
        const gen = res.generations[0];
        if (!gen) throw new Error(res.errors[0] ?? "сервер не создал генерацию");
        replaceRow(await markLaunched(row.dress_id, gen.generation_id));
      },
      {
        shouldStop: () => stop.current,
        onStep: (i, s) => {
          all.push(s);
          setProgress({ i: i + 1, of: items.length });
        },
      },
    );
    setProgress(null);
    setRunning(false);
    setSteps(all);
    setStopped(out.stopped);
    void client.invalidateQueries({ queryKey: KEY });
  };

  const sum = steps ? summarizePairs(steps) : null;
  return (
    <div className={clsx("fixed inset-x-0 bottom-0 z-20 border-t border-border bg-bg", hidden && "hidden")}>
      <div className="mx-auto flex max-w-[1000px] flex-wrap items-center gap-3 px-5 py-2.5">
        <div className="mr-auto grid gap-0.5 text-[12.5px] text-fg-muted">
          {progress ? <span className="tnum">Запускаю {progress.i} из {progress.of}…</span> : null}
          {sum ? (
            <span className="tnum">
              {stopped ? "Остановлено. " : ""}Запущено {sum.ok}
              {sum.failed.length ? `, ошибок ${sum.failed.length}` : ""}
            </span>
          ) : null}
          {sum?.failed.map((f) => (
            <span key={f.dress_id} className="text-[12px] text-warn">
              {names.get(f.dress_id)}: {f.error}
            </span>
          ))}
          {picking ? <span>Идёт подбор — запуск после него.</span> : null}
          {est.error ? <span className="text-warn">оценка не получилась: {errMsg(est.error)}</span> : null}
        </div>
        {running ? (
          <button type="button" className={btn} onClick={() => (stop.current = true)}>
            Остановить
          </button>
        ) : null}
        <button type="button" className={clsx(btnPrimary, "ml-auto")} disabled={running || picking || approved.length === 0} onClick={() => void start()}>
          {running ? <Sparkles size={14} /> : <Play size={14} />}
          Сгенерировать одобренные {approved.length}
          {approved.length ? ` ≈ ${est.data !== undefined ? money(est.data) : "…"}` : ""}
        </button>
      </div>
    </div>
  );
}
